// Generates the scripts/bundles for the local database sync feature.
// Used by both the PC (Python) flow and the Synology NAS (Docker) flow.

export function buildSyncPy(endpoint, token, interval) {
  const ep = endpoint || 'YOUR_ENDPOINT_URL';
  const tk = token || 'YOUR_SYNC_TOKEN';
  const iv = interval || 60;
  return `#!/usr/bin/env python3
"""
Motion Reality — Local Database Mirror
Fetches the website data-export endpoint and writes all data into a local
SQLite database (motion_reality.db).

Each entity is written to its own table with REAL named columns (name, price,
status, stock_quantity, etc.) so you can sort, filter and query it directly in
DB Browser for SQLite — plus a _raw column holding the full JSON for anything
not promoted to a column. Summary report views (v_low_stock, v_top_customers,
v_bookings_by_status, ...) are (re)built after every sync.

By default it does ONE sync and exits — perfect for Synology Task Scheduler
(set it to run every 1 minute). Add --loop to run continuously instead
(useful on a PC where you just leave it in a terminal).

Requirements: Python 3.8+ (no external packages needed)
"""
import sqlite3, json, time, urllib.request, urllib.error, os, sys
from datetime import datetime, timezone

ENDPOINT  = os.environ.get("ENDPOINT", "${ep}")
TOKEN     = os.environ.get("TOKEN", "${tk}")
INTERVAL  = int(os.environ.get("INTERVAL", "${iv}"))
SCRIPT_DIR = os.path.dirname(os.path.abspath(__file__))
DATA_DIR   = os.environ.get("DATA_DIR", os.path.join(SCRIPT_DIR, "data"))
DB_PATH    = os.environ.get("DB_PATH", os.path.join(DATA_DIR, "motion_reality.db"))
LOG_PATH   = os.environ.get("LOG_PATH", os.path.join(SCRIPT_DIR, "sync.log"))
LOOP       = ("--loop" in sys.argv) or (os.environ.get("LOOP", "").lower() in ("1", "true", "yes"))

# Built-in columns present on every record.
BASE_COLS = [("id", "TEXT PRIMARY KEY"), ("created_date", "TEXT"), ("updated_date", "TEXT"), ("created_by_id", "TEXT")]
RAW_COL   = [("_raw", "TEXT")]

# Per-entity columns promoted to real, typed columns. Entities not listed here
# fall back to a (id, created_date, updated_date, created_by_id, _raw) table.
COLUMN_MAP = {
    "Product": [
        ("name", "TEXT"), ("sku", "TEXT"), ("price", "REAL"), ("category", "TEXT"),
        ("reporting_category", "TEXT"), ("stock_quantity", "REAL"), ("stockable", "INTEGER"),
        ("in_stock", "INTEGER"), ("published", "INTEGER"), ("manual_product", "INTEGER"),
        ("featured", "INTEGER"), ("discounts_disabled", "INTEGER"), ("image_url", "TEXT"),
        ("seo_title", "TEXT"), ("permalink", "TEXT"), ("custom_slug", "TEXT"),
        ("square_item_id", "TEXT"), ("square_variation_id", "TEXT"), ("ebay_listed", "INTEGER"),
    ],
    "Category": [("key", "TEXT"), ("label", "TEXT"), ("sort_order", "REAL"), ("active", "INTEGER")],
    "Order": [
        ("customer_name", "TEXT"), ("customer_email", "TEXT"), ("customer_phone", "TEXT"),
        ("product_id", "TEXT"), ("product_name", "TEXT"), ("quantity", "REAL"),
        ("amount", "REAL"), ("shipping_amount", "REAL"), ("status", "TEXT"),
        ("tracking_number", "TEXT"), ("source", "TEXT"), ("discount_code", "TEXT"),
        ("discount_amount", "REAL"), ("refunded_amount", "REAL"), ("invoice_number", "TEXT"),
        ("square_order_id", "TEXT"), ("square_payment_id", "TEXT"),
    ],
    "User": [("full_name", "TEXT"), ("email", "TEXT"), ("role", "TEXT")],
    "Booking": [
        ("customer_name", "TEXT"), ("customer_email", "TEXT"), ("customer_phone", "TEXT"),
        ("experience_type", "TEXT"), ("rig_type", "TEXT"), ("date", "TEXT"),
        ("time_slot", "TEXT"), ("duration", "REAL"), ("status", "TEXT"),
        ("total_price", "REAL"), ("is_loyalty_free", "INTEGER"),
    ],
    "BookingExperience": [
        ("key", "TEXT"), ("name", "TEXT"), ("price", "REAL"), ("duration_minutes", "REAL"),
        ("requires_rig", "INTEGER"), ("active", "INTEGER"), ("sort_order", "REAL"),
    ],
    "BookingRig": [("key", "TEXT"), ("name", "TEXT"), ("active", "INTEGER"), ("sort_order", "REAL")],
    "ShowcaseRig": [
        ("name", "TEXT"), ("slug", "TEXT"), ("category", "TEXT"), ("featured", "INTEGER"),
        ("overall_rating", "REAL"), ("sort_order", "REAL"), ("booking_url", "TEXT"),
    ],
    "ShowcaseReview": [("rig_id", "TEXT"), ("author_name", "TEXT"), ("rating", "REAL"), ("verified", "INTEGER"), ("sort_order", "REAL")],
    "Review": [
        ("author_name", "TEXT"), ("rating", "REAL"), ("review_category", "TEXT"),
        ("status", "TEXT"), ("source", "TEXT"), ("verified", "INTEGER"),
        ("product_id", "TEXT"), ("is_draw_winner", "INTEGER"), ("sort_order", "REAL"),
    ],
    "ExperienceFeedback": [
        ("first_name", "TEXT"), ("last_name", "TEXT"), ("email", "TEXT"),
        ("booking_reference", "TEXT"), ("experience_type", "TEXT"), ("date_of_experience", "TEXT"),
        ("q_overall", "REAL"), ("q_recommend", "REAL"), ("status", "TEXT"),
        ("consent_marketing", "INTEGER"),
    ],
    "SessionResult": [
        ("customer_name", "TEXT"), ("customer_email", "TEXT"), ("experience_type", "TEXT"),
        ("rig_type", "TEXT"), ("session_date", "TEXT"), ("track", "TEXT"),
        ("fastest_lap_time", "TEXT"), ("top_speed_kph", "REAL"), ("laps_completed", "REAL"),
        ("final_position", "REAL"),
    ],
    "GiftCard": [
        ("code", "TEXT"), ("initial_amount", "REAL"), ("balance", "REAL"), ("status", "TEXT"),
        ("source", "TEXT"), ("experience_type", "TEXT"), ("purchaser_email", "TEXT"),
        ("recipient_email", "TEXT"), ("delivery_method", "TEXT"), ("expiry_date", "TEXT"),
    ],
    "DiscountCode": [
        ("code", "TEXT"), ("type", "TEXT"), ("value", "REAL"), ("active", "INTEGER"),
        ("expiry_date", "TEXT"), ("min_spend", "REAL"), ("max_uses", "REAL"),
        ("used_count", "REAL"), ("one_per_customer", "INTEGER"),
    ],
    "ShippingRate": [
        ("name", "TEXT"), ("zone", "TEXT"), ("min_weight_g", "REAL"), ("max_weight_g", "REAL"),
        ("rate", "REAL"), ("active", "INTEGER"), ("sort_order", "REAL"),
    ],
    "CustomerTask": [
        ("customer_email", "TEXT"), ("customer_name", "TEXT"), ("title", "TEXT"),
        ("status", "TEXT"), ("priority", "TEXT"), ("due_date", "TEXT"), ("assigned_to", "TEXT"),
    ],
    "CustomerTag": [("key", "TEXT"), ("label", "TEXT"), ("color", "TEXT"), ("active", "INTEGER"), ("sort_order", "REAL")],
    "RigLiveFeed": [
        ("rig_key", "TEXT"), ("rig_name", "TEXT"), ("status", "TEXT"), ("is_live", "INTEGER"),
        ("sim", "TEXT"), ("driver_name", "TEXT"), ("track", "TEXT"), ("car", "TEXT"),
        ("lap_time", "TEXT"), ("best_lap_time", "TEXT"), ("speed_kph", "REAL"), ("last_update", "TEXT"),
    ],
}

# Ready-made report views, recreated after every sync so they always reflect
# the latest columns. Open these directly in DB Browser for SQLite.
VIEWS = {
    "v_low_stock": "SELECT name, sku, category, stock_quantity, price FROM Product WHERE stockable=1 AND published=1 AND stock_quantity IS NOT NULL AND stock_quantity <= 5 ORDER BY stock_quantity ASC, name",
    "v_published_products": "SELECT name, sku, price, category, stock_quantity, image_url FROM Product WHERE published=1 ORDER BY category, name",
    "v_orders_by_status": 'SELECT status, COUNT(*) AS order_count, COALESCE(SUM(amount),0) AS total_gbp FROM "Order" GROUP BY status ORDER BY total_gbp DESC',
    "v_orders_this_month": 'SELECT customer_name, customer_email, product_name, quantity, amount, status, created_date FROM "Order" WHERE substr(created_date,1,7) = strftime("%Y-%m","now") ORDER BY created_date DESC',
    "v_top_customers": 'SELECT customer_email, COUNT(*) AS orders, COALESCE(SUM(amount),0) AS total_spent_gbp FROM "Order" WHERE customer_email IS NOT NULL AND customer_email != "" GROUP BY customer_email ORDER BY total_spent_gbp DESC',
    "v_bookings_by_status": "SELECT status, COUNT(*) AS count, COALESCE(SUM(total_price),0) AS revenue_gbp FROM Booking GROUP BY status ORDER BY revenue_gbp DESC",
    "v_bookings_by_rig": "SELECT rig_type, COUNT(*) AS count, COALESCE(SUM(total_price),0) AS revenue_gbp FROM Booking GROUP BY rig_type ORDER BY count DESC",
    "v_recent_bookings": "SELECT customer_name, customer_email, experience_type, rig_type, date, time_slot, status, total_price FROM Booking ORDER BY date DESC, time_slot DESC",
    "v_gift_cards_outstanding": "SELECT code, balance, status, purchaser_email, recipient_email, expiry_date FROM GiftCard WHERE status='active' AND balance > 0 ORDER BY balance DESC",
    "v_reviews_pending": "SELECT author_name, rating, review_category, status, source, product_id FROM Review WHERE status='pending' ORDER BY created_date DESC",
    "v_feedback_pending": "SELECT first_name, last_name, email, experience_type, date_of_experience, q_overall, status FROM ExperienceFeedback WHERE status='pending' ORDER BY created_date DESC",
    "v_session_results_recent": "SELECT customer_name, experience_type, rig_type, session_date, track, fastest_lap_time, top_speed_kph, laps_completed, final_position FROM SessionResult ORDER BY session_date DESC",
}

def safe_name(name):
    return name.replace("]", "_").replace("[", "_")

def coerce(v):
    # Booleans become 0/1 so they land in INTEGER columns cleanly.
    if isinstance(v, bool):
        return 1 if v else 0
    return v

def log(msg):
    ts = datetime.now(timezone.utc).strftime("%Y-%m-%d %H:%M:%S")
    line = "[{}] {}".format(ts, msg)
    print(line, flush=True)
    try:
        with open(LOG_PATH, "a", encoding="utf-8") as f:
            f.write(line + "\\n")
    except Exception:
        pass

def ensure_tables(conn, entities):
    cur = conn.cursor()
    for name in entities.keys():
        tname = safe_name(name)
        cols = COLUMN_MAP.get(name, [])
        all_cols = BASE_COLS + cols + RAW_COL
        coldefs = ", ".join('"{}" {}'.format(c, t) for c, t in all_cols)
        cur.execute('CREATE TABLE IF NOT EXISTS [{}] ({})'.format(tname, coldefs))
        # Migrate: add any new columns to an already-existing table.
        existing = {row[1] for row in cur.execute('PRAGMA table_info([{}])'.format(tname)).fetchall()}
        for c, t in all_cols:
            if c not in existing and c != "id":
                cur.execute('ALTER TABLE [{}] ADD COLUMN "{}" {}'.format(tname, c, t))
    conn.commit()

def upsert(conn, entities):
    cur = conn.cursor()
    for name, rows in entities.items():
        if not rows:
            continue
        tname = safe_name(name)
        field_names = [c for c, _ in COLUMN_MAP.get(name, [])]
        col_list = ["id", "created_date", "updated_date", "created_by_id"] + field_names + ["_raw"]
        placeholders = ",".join("?" for _ in col_list)
        quoted = ",".join('"{}"'.format(c) for c in col_list)
        sql = 'INSERT OR REPLACE INTO [{}] ({}) VALUES ({})'.format(tname, quoted, placeholders)
        for row in rows:
            vals = [row.get("id"), row.get("created_date"), row.get("updated_date"), row.get("created_by_id")]
            vals += [coerce(row.get(f)) for f in field_names]
            vals.append(json.dumps(row, default=str))
            cur.execute(sql, vals)
    conn.commit()

def create_views(conn):
    cur = conn.cursor()
    for vname, sql in VIEWS.items():
        cur.execute('DROP VIEW IF EXISTS [{}]'.format(vname))
        cur.execute('CREATE VIEW [{}] AS {}'.format(vname, sql))
    conn.commit()
    log("Built {} report views.".format(len(VIEWS)))

def fetch(since):
    url = ENDPOINT + ("?since=" + since if since else "")
    req = urllib.request.Request(url, headers={
        "x-sync-token": TOKEN,
        "User-Agent": "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/124.0 Safari/537.36",
        "Accept": "application/json",
    })
    with urllib.request.urlopen(req, timeout=60) as resp:
        return json.loads(resp.read().decode("utf-8"))

def sync_once(conn, since):
    log("Fetching " + ENDPOINT + ("?since=" + since if since else ""))
    data = fetch(since)
    ents = data.get("entities", {})
    ensure_tables(conn, ents)
    upsert(conn, ents)
    create_views(conn)
    counts = {k: len(v) for k, v in ents.items() if v}
    total = sum(counts.values())
    log("Synced {} records across {} tables -> {}".format(total, len(counts), DB_PATH))
    return data.get("synced_at", "")

def main():
    os.makedirs(DATA_DIR, exist_ok=True)
    log("=== Motion Reality sync starting ===")
    log("Script dir : " + SCRIPT_DIR)
    log("DB path    : " + DB_PATH)
    log("Log path    : " + LOG_PATH)
    log("Endpoint   : " + ENDPOINT)
    log("Token set  : " + ("yes" if TOKEN and TOKEN != "YOUR_SYNC_TOKEN" else "NO"))
    log("Loop mode  : " + ("yes" if LOOP else "no (single run)"))
    conn = sqlite3.connect(DB_PATH)
    if LOOP:
        log("Looping every {}s.".format(INTERVAL))
        since = ""
        while True:
            try:
                since = sync_once(conn, since)
            except urllib.error.HTTPError as e:
                body = e.read()[:500].decode("utf-8", "replace")
                log("HTTP error {}: {}".format(e.code, body))
            except Exception as e:
                log("Error: {} {}".format(type(e).__name__, e))
            time.sleep(INTERVAL)
    else:
        try:
            sync_once(conn, "")
            log("Done.")
        except urllib.error.HTTPError as e:
            body = e.read()[:500].decode("utf-8", "replace")
            log("HTTP error {}: {}".format(e.code, body))
            sys.exit(1)
        except Exception as e:
            log("Error: {} {}".format(type(e).__name__, e))
            sys.exit(1)

if __name__ == "__main__":
    main()
`;
}

export function buildDockerfile() {
  return `FROM python:3.11-slim
WORKDIR /app
COPY sync.py .
CMD ["python", "sync.py"]
`;
}

export function buildDockerCompose(endpoint, token, interval) {
  const ep = endpoint || 'YOUR_ENDPOINT_URL';
  const tk = token || 'YOUR_SYNC_TOKEN';
  const iv = interval || 60;
  return `services:
  motion-reality-sync:
    build: .
    container_name: motion-reality-sync
    restart: unless-stopped
    environment:
      ENDPOINT: "${ep}"
      TOKEN: "${tk}"
      INTERVAL: "${iv}"
      LOOP: "1"
      DB_PATH: /data/motion_reality.db
    volumes:
      - ./data:/data
`;
}

// Single bash script that writes all three files into a folder on the NAS.
export function buildSetupSh(endpoint, token, interval) {
  const syncPy = buildSyncPy(endpoint, token, interval);
  const dockerfile = buildDockerfile();
  const compose = buildDockerCompose(endpoint, token, interval);
  // Use quoted heredoc delimiters so nothing inside is expanded by bash.
  const inject = (s, delim) => s.replace(/(\r?\n)$/, '');
  return `#!/bin/bash
# Motion Reality — Synology NAS Docker setup
# Creates a folder with the sync container files, ready for Container Manager.
# Run via SSH or Synology Task Scheduler (Control Panel > Task Scheduler).
set -e
DIR="motion-reality-sync"
mkdir -p "$DIR/data"
cd "$DIR"

cat > sync.py <<'MR_SYNC_PY_EOF'
${inject(syncPy)}MR_SYNC_PY_EOF

cat > Dockerfile <<'MR_DOCKERFILE_EOF'
${inject(dockerfile)}MR_DOCKERFILE_EOF

cat > docker-compose.yml <<'MR_COMPOSE_EOF'
${inject(compose)}MR_COMPOSE_EOF

echo "Done. Files created in: $(pwd)"
echo "Next steps:"
echo "  1) Open Container Manager > Container > Create."
echo "  2) Choose 'Open project' and select the motion-reality-sync folder."
echo "  3) Build and start. The database file appears in ./data/motion_reality.db"
echo "Or from SSH:  docker compose up -d --build"
`;
}